import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmationConfig } from '@fuse/services/confirmation/confirmation.types';
import { FuseConfirmationDialogComponent } from '@fuse/services/confirmation/dialog/dialog.component';
import { merge } from 'lodash-es';
import {TranslocoService} from "@ngneat/transloco";

@Injectable({providedIn: 'root'})
export class FuseConfirmationService
{
    private _matDialog: MatDialog = inject(MatDialog);
    private _transloco: TranslocoService = inject(TranslocoService);
    private _defaultConfig: FuseConfirmationConfig = {
        title      : 'Confirm action',
        message    : 'Are you sure you want to confirm this action?',
        icon       : {
            show : true,
            name : 'heroicons_outline:exclamation-triangle',
            color: 'warn',
        },
        actions    : {
            confirm: {
                show : true,
                label: 'Confirm',
                color: 'warn',
            },
            cancel : {
                show : true,
                label: 'Cancel',
            },
        },
        dismissible: false,
    };

    /**
     * Constructor
     */
    constructor()
    {

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * The method opens a dialog containing provided messages. Note that the message is automatically translated.
     *
     * @param config - Config object describing the dialog.
     * @param placeholders - Placeholders to be used after message is tranlated. Is an array of placeholders that are splited with |||, like this: ["{msg1}|||User Name", "{title}|||48"]. ||| is mandatory for each array entry, while tags can have whatever form user likes.
     *                       Note that placeholders are replaced in title, message and actions (confirm and cancel)
     */

    open(config: FuseConfirmationConfig = {}, placeholders?:string[]): MatDialogRef<FuseConfirmationDialogComponent>
    {
        // Merge the user config with the default config
        const userConfig = merge({}, this._defaultConfig, config);

        userConfig.title = this.translate(userConfig.title, placeholders);
        userConfig.message = this.translate(userConfig.message, placeholders);
        userConfig.actions.confirm.label = this.translate(userConfig.actions.confirm.label, placeholders);
        userConfig.actions.cancel.label = this.translate(userConfig.actions.cancel.label, placeholders);

        // Open the dialog
        return this._matDialog.open(FuseConfirmationDialogComponent, {
            autoFocus   : false,
            disableClose: !userConfig.dismissible,
            data        : userConfig,
            panelClass  : 'fuse-confirmation-dialog-panel',
        });
    }

    private translate(msg, placeholders:string[]):string{
        let translated: string = msg;
        translated = this._transloco.translate(msg);
        if(!placeholders || placeholders.length == 0){
            return translated;
        }

        placeholders.forEach(placeholder =>{
            const placeholder_dataPair = placeholder.split("|||");
            if(placeholder_dataPair.length == 2) {
                translated = translated.replace(placeholder_dataPair[0], placeholder_dataPair[1]);
            }
        })
        return translated;
    }

    showConfirmDialog(title:string, msg:string, actionBut: string, cancelBut:string, confirmHandler: () => void, dismissHandler: () => void, placeholders?:string[]) {
        let confirm:FuseConfirmationConfig = {
            "title": title,
            "message": msg,
            "icon": {
                "show": true,
                "name": "heroicons_outline:question-mark-circle",
                "color": "primary"
            },
            "actions": {
                "confirm": {
                    "show": true,
                    "label": actionBut,
                    "color": "primary"
                },
                "cancel": {
                    "show": true,
                    "label": cancelBut
                }
            },
            "dismissible": true
        }

        // Open the confirmation and save the reference
        const dialogRef = this.open(confirm, placeholders);
        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if(result == "confirmed"){
                if(confirmHandler) {
                    confirmHandler();
                }
            }else {
                if(dismissHandler){
                    dismissHandler();
                }
            }
        })
    }

}
